// Matches the single-line editor's own left padding (see `--single-line`
// in editor-template.scss) — kept as the minimum gap on both sides so the
// caret never scrolls flush against the edge.
const EDGE_PADDING_PX = 10

type CaretRect = {left: number, right: number, top: number}

function isDegenerate(rect: DOMRect): boolean {
    return rect.left === 0 && rect.right === 0 && rect.top === 0 && rect.bottom === 0
}

// A collapsed caret positioned right after the LAST node in the editor —
// e.g. inside the zero-width trailing spacer after the last chip, with
// nothing after it — can report an all-zero bounding rect in some
// browsers: there's no glyph run left to hang caret metrics off at the
// very end of the content. Fall back to the nearest preceding element's
// right edge, which is exactly where the caret visually sits in that case
// (this is what made the last chip never scroll into view).
function fallbackCaretRect(range: Range): CaretRect | null {
    const {startContainer, startOffset} = range

    let before: Node | null = startContainer.nodeType === Node.TEXT_NODE
        ? startContainer.previousSibling
        : (startOffset > 0 ? startContainer.childNodes[startOffset - 1] : null)

    while (before && before.nodeType !== Node.ELEMENT_NODE) {
        before = before.previousSibling
    }

    if (!before) return null

    const rect = (before as Element).getBoundingClientRect()
    return {left: rect.right, right: rect.right, top: rect.top}
}

// Keeps the caret within the visible scroll area of a horizontally
// scrolling single-line editor (`overflow: hidden` + `white-space: nowrap`
// — see `--single-line` in editor-template.scss). `overflow: hidden`
// suppresses the browser's own "scroll caret into view" behaviour, so
// arrow-key / Home / End navigation would otherwise let the caret wander
// outside the visible area with no visual feedback. Called on every
// selection change while `singleLine` is active.
export function textareaWithVariables_scrollCaretIntoView(editor: HTMLElement): void {
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) {
        return
    }

    const range = sel.getRangeAt(0)
    if (!editor.contains(range.startContainer)) {
        return
    }

    let caretRect: CaretRect = range.getBoundingClientRect()

    if (isDegenerate(caretRect as DOMRect)) {
        const fallback = fallbackCaretRect(range)
        if (!fallback) return
        caretRect = fallback
    }

    const editorRect = editor.getBoundingClientRect()
    const leftBound   = editorRect.left  + EDGE_PADDING_PX
    const rightBound  = editorRect.right - EDGE_PADDING_PX

    if (caretRect.left < leftBound) {
        editor.scrollLeft -= (leftBound - caretRect.left)
    } else if (caretRect.right > rightBound) {
        editor.scrollLeft += (caretRect.right - rightBound)
    }
}
